import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { FileText, Download, User, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

// Sous-composants
import {
  EventInfoCard,
  StatusSummary,
  IntervenantSignature,
  ParticipantCard,
  InviteCard
} from './emargement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===== HOOKS PERSONNALISÉS =====

const useParticipants = (event, open) => {
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    if (open && event) {
      // Le backend renvoie désormais toujours statut_presence parmi les valeurs
      // canoniques de l'enum StatutPresence ('non_defini', 'present',
      // 'absent_justifie', 'absent_non_justifie'). Plus de dérivation runtime.
      const initialParticipants = (event.participants || []).map(p => ({
        ...p,
        statut_presence: p.statut_presence || 'non_defini',
        signature: p.signature || null,
        justificatif: p.justificatif || null
      }));
      setParticipants(initialParticipants);
    }
  }, [open, event]);

  const handleStatutChange = (participantId, newStatut) => {
    setParticipants(prev => prev.map(p => 
      p.id === participantId 
        ? { 
            ...p, 
            statut_presence: newStatut,
            signature: newStatut !== 'present' ? null : p.signature,
            present: newStatut === 'present'
          }
        : p
    ));
  };

  const updateParticipantSignature = (participantId, signature) => {
    setParticipants(prev => prev.map(p =>
      p.id === participantId ? { ...p, signature } : p
    ));
  };

  const updateParticipantJustificatif = (participantId, justificatif_id, justificatif_nom) => {
    setParticipants(prev => prev.map(p =>
      p.id === participantId ? { ...p, justificatif_id, justificatif_nom } : p
    ));
  };

  return { 
    participants, 
    setParticipants,
    handleStatutChange, 
    updateParticipantSignature,
    updateParticipantJustificatif
  };
};

const useInvites = (event, open) => {
  const [invites, setInvites] = useState([]);

  useEffect(() => {
    if (open && event) {
      const initialInvites = (event.invites || []).map(inv => ({
        ...inv,
        present: inv.present || false,
        signature: inv.signature || null
      }));
      setInvites(initialInvites);
    }
  }, [open, event]);

  const handlePresenceChange = (inviteId, present) => {
    setInvites(prev => prev.map(inv =>
      inv.id === inviteId ? { ...inv, present } : inv
    ));
  };

  const updateInviteSignature = (inviteId, signature, present = true) => {
    setInvites(prev => prev.map(inv =>
      inv.id === inviteId ? { ...inv, signature, present } : inv
    ));
  };

  return { invites, setInvites, handlePresenceChange, updateInviteSignature };
};

// ===== UTILITAIRES =====

const calculateDuration = (event) => {
  if (!event?.heure_debut || !event?.heure_fin) return 'N/A';
  try {
    const [startH, startM] = event.heure_debut.split(':').map(Number);
    const [endH, endM] = event.heure_fin.split(':').map(Number);
    const minutes = (endH * 60 + endM) - (startH * 60 + startM);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}`;
  } catch {
    return 'N/A';
  }
};

const getStatusCounts = (participants) => {
  return participants.reduce((acc, p) => {
    acc[p.statut_presence] = (acc[p.statut_presence] || 0) + 1;
    return acc;
  }, {});
};

// ===== COMPOSANT PRINCIPAL =====

const EmargementDialog = ({ 
  open, 
  onOpenChange, 
  event, 
  eventType = 'atelier',
  onUpdate
}) => {
  const [intervenantSignature, setIntervenantSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadingJustificatif, setUploadingJustificatif] = useState(null);

  // Hooks personnalisés
  const { 
    participants, 
    handleStatutChange, 
    updateParticipantSignature,
    updateParticipantJustificatif 
  } = useParticipants(event, open);
  
  const { 
    invites, 
    handlePresenceChange, 
    updateInviteSignature 
  } = useInvites(event, open);

  // Initialiser la signature intervenant
  useEffect(() => {
    if (open && event) {
      setIntervenantSignature(event.signature_animateur || null);
    }
  }, [open, event]);

  // ===== HANDLERS =====

  // signatureData est désormais fourni par les sous-composants (SignatureBlock
  // gère son propre canvas et sa propre ref en interne).
  const handleSaveSignature = (targetId, signatureData) => {
    if (!signatureData) return;
    if (targetId === 'intervenant') {
      setIntervenantSignature(signatureData);
      toast.success('Signature intervenant enregistrée');
    } else if (targetId.startsWith('invite_')) {
      const inviteId = targetId.replace('invite_', '');
      updateInviteSignature(inviteId, signatureData);
      toast.success('Signature invité enregistrée');
    } else {
      updateParticipantSignature(targetId, signatureData);
      toast.success('Signature bénéficiaire enregistrée');
    }
  };

  const handleClearSignature = (targetId) => {
    if (targetId === 'intervenant') {
      setIntervenantSignature(null);
    } else if (targetId.startsWith('invite_')) {
      const inviteId = targetId.replace('invite_', '');
      updateInviteSignature(inviteId, null, true);
    } else {
      updateParticipantSignature(targetId, null);
    }
    toast.info('Signature effacée');
  };

  const handleUploadJustificatif = async (participantId, file) => {
    if (!file || !event) return;
    
    setUploadingJustificatif(participantId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('event_id', event.id);
      formData.append('participant_id', participantId);
      
      const response = await api.post('/emargement/justificatif', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      updateParticipantJustificatif(participantId, response.data.id, file.name);
      toast.success('Justificatif téléversé avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléversement');
    } finally {
      setUploadingJustificatif(null);
    }
  };

  const handleDownloadJustificatif = async (justificatifId, filename) => {
    try {
      const response = await api.get(`/emargement/justificatif/${justificatifId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'justificatif';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = eventType === 'atelier' 
        ? `/ateliers/${event.id}/emargement`
        : `/actions/${event.id}/emargement`;

      await api.put(endpoint, {
        signature_animateur: intervenantSignature,
        participants: participants.map(p => ({
          id: p.id,
          statut_presence: p.statut_presence,
          signature: p.signature,
          present: p.statut_presence === 'present'
        })),
        invites: invites.map(inv => ({
          id: inv.id,
          present: inv.present,
          signature: inv.signature
        }))
      });

      toast.success('Émargement enregistré avec succès');
      onUpdate?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Erreur save:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadFeuille = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = eventType === 'atelier' 
        ? `/reporting/feuille-emargement/atelier/${event.id}`
        : `/reporting/feuille-emargement/action/${event.id}`;

      const response = await fetch(`${API_URL}/api${endpoint}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur de génération');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feuille_emargement_${event.titre || event.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Feuille d\'émargement téléchargée');
    } catch (error) {
      toast.error('Erreur de téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  // ===== RENDU =====

  if (!event) return null;

  const statusCounts = getStatusCounts(participants);
  const duration = calculateDuration(event);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl rounded-3xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <FileText className="w-6 h-6 text-blue-600" />
            Feuille d'émargement
          </DialogTitle>
          <DialogDescription>
            Gérez les présences et signatures pour cet événement
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 py-4 pr-2">
          {/* Informations de l'événement */}
          <EventInfoCard event={event} duration={duration} />

          {/* Résumé des présences */}
          <StatusSummary statusCounts={statusCounts} />

          {/* Signature de l'intervenant */}
          <IntervenantSignature
            signature={intervenantSignature}
            participants={participants}
            onSaveSignature={handleSaveSignature}
            onClearSignature={handleClearSignature}
          />

          {/* Liste des participants */}
          <div className="space-y-3">
            <Label className="font-bold text-slate-900 text-base">
              Participants ({participants.length})
            </Label>

            <div className="space-y-3">
              {participants.map((participant) => (
                <ParticipantCard
                  key={participant.id}
                  participant={participant}
                  onStatutChange={handleStatutChange}
                  onUploadJustificatif={handleUploadJustificatif}
                  onDownloadJustificatif={handleDownloadJustificatif}
                  uploadingJustificatif={uploadingJustificatif}
                />
              ))}

              {participants.length === 0 && (
                <p className="text-center text-slate-500 py-8">
                  Aucun participant inscrit à cet événement
                </p>
              )}
            </div>
          </div>

          {/* Section Invités */}
          {invites.length > 0 && (
            <div className="space-y-3">
              <Label className="font-bold text-slate-900 text-base flex items-center gap-2">
                <User className="w-4 h-4 text-purple-600" />
                Invités ({invites.length})
                <span className="text-xs font-normal text-slate-500">(Financeurs, Référents, Intervenants)</span>
              </Label>

              <div className="space-y-3">
                {invites.map((invite) => (
                  <InviteCard
                    key={invite.id}
                    invite={invite}
                    onPresenceChange={handlePresenceChange}
                    onSaveSignature={handleSaveSignature}
                    onClearSignature={handleClearSignature}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadFeuille}
            disabled={downloading}
            className="rounded-xl"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger PDF
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
              Fermer
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmargementDialog;
